import { RegulatoryComplianceFixButton } from '@/features/templateBuilder/templateDetails/components/regulatoryComplianceFixButton';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { regulatoryGuidelineComplianceReasonAtom } from '@heidi/templates';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ExternalLinkIcon, EyeIcon } from 'lucide-react';
import Link from 'next/link';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '../mobile-responsive-dialog-drawer';
import { regulatoryGuidelineWarningModalOpenAtom } from './regulatory-guideline-warning-modal.atoms';

interface RegulatoryGuidelineWarningModalProps {
  onSaveAnyway: () => void;
  onReturnEditor?: () => void;
  showFixForMe?: boolean;
  /**
   * Uploaded forms have no in-product editor for the flagged `<guidance>`.
   * These variants name the actions that work and show the flagged fragment
   * back to the clinician who uploaded it. That string can be clinical text:
   * render it, never log it.
   */
  variant?: 'default' | 'xml-form' | 'json-form';
}

export const RegulatoryGuidelineWarningModal = ({
  onSaveAnyway,
  onReturnEditor,
  showFixForMe = false,
  variant = 'default',
}: RegulatoryGuidelineWarningModalProps) => {
  const [isOpen, setIsOpen] = useAtom(regulatoryGuidelineWarningModalOpenAtom);
  const flaggedGuidance = useAtomValue(regulatoryGuidelineComplianceReasonAtom);
  const setFlaggedGuidance = useSetAtom(regulatoryGuidelineComplianceReasonAtom);
  const isUploadedForm = variant === 'xml-form' || variant === 'json-form';

  const close = useCallback(() => {
    setIsOpen(false);
    if (isUploadedForm) {
      setFlaggedGuidance(null);
    }
  }, [isUploadedForm, setFlaggedGuidance, setIsOpen]);

  const handleSaveAnyway = useCallback(() => {
    onSaveAnyway();
    close();
  }, [close, onSaveAnyway]);

  const handleReturnEditor = useCallback(() => {
    onReturnEditor?.();
    close();
  }, [close, onReturnEditor]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
        return;
      }
      setIsOpen(true);
    },
    [close, setIsOpen]
  );

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange} dismissible={false}>
      <DialogDrawerContent
        className="flex flex-col gap-4 p-4"
        hideCloseIcon
        dismissible={false}
      >
        <FramedIcon size="lg" state="warning" icon={<EyeIcon />} />
        <div className="flex flex-col gap-2">
          <DialogDrawerTitle className="text-validation-warning">
            {isUploadedForm ? (
              <FormattedMessage
                defaultMessage="Review this form’s guidance before saving"
                id="2nbxMn1i48"
                description="Title of the regulatory warning shown when saving an XML form"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Please review your template to ensure it meets regulatory guidelines"
                id="t1NJ55RcF8"
                description="Title of the regulatory guideline warning shown when saving a template"
              />
            )}
          </DialogDrawerTitle>
          <DialogDrawerDescription>
            {variant === 'json-form' ? (
              <FormattedMessage
                defaultMessage="Heidi flagged guidance in the uploaded JSON that may ask for clinical support the regulatory guidelines do not allow. Heidi cannot edit that guidance on this screen. Amend the JSON file and upload it again, or save anyway and accept this finding."
                id="p7p71Izi2b"
                description="Explains that a JSON form’s flagged guidance is changed by re-uploading the file, or accepted with save anyway"
              />
            ) : variant === 'xml-form' ? (
              <FormattedMessage
                defaultMessage="Heidi flagged guidance in the uploaded XML that may ask for clinical support the regulatory guidelines do not allow. Heidi cannot edit that guidance on this screen. Amend the XML file and upload it again, or save anyway and accept this finding."
                id="UOMJQlYWxY"
                description="Explains that an XML form’s flagged guidance is changed by re-uploading the file, or accepted with save anyway"
              />
            ) : (
              <FormattedMessage
                defaultMessage="This template may contain instructions or requests for clinical support. To satisfy regulatory requirements, please review your template and remove any instructions for generative diagnoses, management plans, medical referrals and calculators."
                id="LtIadMJuoF"
                description="Explanation of why a template must be reviewed before it can be saved"
              />
            )}
          </DialogDrawerDescription>
        </div>
        {isUploadedForm && flaggedGuidance ? (
          <div className="flex flex-col gap-1">
            <TypographyP2Bold>
              <FormattedMessage
                defaultMessage="Flagged guidance"
                id="Pd1w9X2+fT"
                description="Heading for the guidance text the compliance check flagged in an uploaded XML form"
              />
            </TypographyP2Bold>
            <div className="scrollbar-subtle max-h-40 overflow-y-auto rounded-md border border-border bg-surface-page p-3">
              <TypographyP2 className="whitespace-pre-wrap break-words">
                {flaggedGuidance}
              </TypographyP2>
            </div>
          </div>
        ) : null}
        <Link
          href="https://www.heidihealth.com/support/en/articles/11998932-using-heidi-within-the-nhs-guidelines"
          target="_blank"
          className="flex flex-row gap-1 self-start text-text-secondary"
        >
          <TypographyP2Bold>
            <FormattedMessage
              defaultMessage="Learn more"
              id="B4f8QCNHKM"
              description="Link to regulatory guideline documentation"
            />
          </TypographyP2Bold>
          <ExternalLinkIcon className="self-center" width={14} height={14} />
        </Link>
        <div className="flex justify-end gap-2 pt-3">
          <Button
            size="lg"
            disableAutomaticSvgSizing
            variant="ghost"
            onClick={handleSaveAnyway}
            disableAutomaticTypographyClasses={true}
          >
            <TypographyP2Bold>
              <FormattedMessage
                defaultMessage="Save anyway"
                id="C5Rf0RdnU9"
                description="Action to save a template despite the regulatory warning"
              />
            </TypographyP2Bold>
          </Button>
          {showFixForMe && (
            <RegulatoryComplianceFixButton isOpen={isOpen} onFixed={close} />
          )}
          <Button
            size="lg"
            disableAutomaticSvgSizing
            onClick={handleReturnEditor}
            disableAutomaticTypographyClasses={true}
          >
            <TypographyP2Bold>
              {isUploadedForm ? (
                <FormattedMessage
                  defaultMessage="Back to form"
                  id="URm7SLsFNQ"
                  description="Dismisses the XML form compliance warning and returns to the form"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Return to editor"
                  id="ys9KTPV8S8"
                  description="Action to dismiss the warning and continue editing the template"
                />
              )}
            </TypographyP2Bold>
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
